import React, { useState } from 'react';
import PropTypes from 'prop-types';

// material ui
import Box from '@material-ui/core/Box';
import MenuItem from '@material-ui/core/MenuItem';
import Button from '@material-ui/core/Button';
import Typography from '@material-ui/core/Typography';
import Select from '@material-ui/core/Select';
import Skeleton from '@material-ui/lab/Skeleton';
import { TextareaAutosize } from '@material-ui/core';

import Styles from '../DataEntry/styles/Review.styles';
import AsyncSelect from './AsyncSelect';

const INPUT_TYPE_TEXT = 'text';
const INPUT_TYPE_SELECT = 'select';
const INPUT_TYPE_MULTI_ASYNC_SELECT = 'multi_async_select';

const FORM_INPUTS = [
  {
    id: 'pno',
    label: 'EU Number',
    placeholder: 'Enter EU Number',
    type: INPUT_TYPE_TEXT
  },
  {
    id: 'drugname',
    label: 'Invented name',
    placeholder: 'Enter Invented name',
    type: INPUT_TYPE_TEXT
  },
  {
    id: 'strength',
    label: 'Strength',
    placeholder: 'Enter Strength',
    type: INPUT_TYPE_TEXT
  },
  {
    id: 'formulations',
    label: 'Pharmaceutical Form',
    type: INPUT_TYPE_MULTI_ASYNC_SELECT,
    entityKey: 'formulations',
    entityLabel: 'Formulation'
  },
  {
    id: 'routes',
    label: 'Route of Administration',
    type: INPUT_TYPE_MULTI_ASYNC_SELECT,
    entityKey: 'routes',
    entityLabel: 'Route'
  },
  {
    id: 'packaging',
    label: 'Packaging',
    placeholder: 'Enter Packaging',
    type: INPUT_TYPE_TEXT
  },
  {
    id: 'concentration',
    label: 'Concentration',
    placeholder: 'Enter Concentration',
    type: INPUT_TYPE_TEXT
  },
  {
    id: 'packsize',
    label: 'Packsize',
    placeholder: 'Enter Packsize',
    type: INPUT_TYPE_TEXT
  },
  {
    id: 'is_active',
    label: 'Is Active',
    placeholder: 'Select Status',
    type: INPUT_TYPE_SELECT,
    options: [
      { label: 'Active', value: true },
      { label: 'Inactive', value: false }
    ]
  },
  {
    id: 'indication_status',
    label: 'Status',
    placeholder: 'Select Status',
    type: INPUT_TYPE_SELECT,
    options: [
      { label: 'Qced', value: 'Qced' },
      { label: 'To Escalate', value: 'To Escalate' },
      { label: 'New Document', value: 'New Document' }
    ]
  }
];

export const FLOW_TYPE_ADD = 'add';
export const FLOW_TYPE_EDIT = 'edit';

/** @type {import('../../types/dtos/ema').AsyncState<never>} */
const DEFAULT_ASYNC_STATE = { data: [], loading: false, error: null };

const normalizeToArray = value => {
  if (Array.isArray(value)) return value;
  if (value) return [value];
  return [];
};

export const EMAProductForm = ({
  data,
  onCancel,
  onSubmit,
  isLoading = false,
  flowType = FLOW_TYPE_ADD,
  /** @type {import('../../types/dtos/ema').AsyncState<import('../../types/dtos/ema').FormulationDto>} */
  formulations = DEFAULT_ASYNC_STATE,
  /** @type {import('../../types/dtos/ema').AsyncState<import('../../types/dtos/ema').RouteDto>} */
  routes = DEFAULT_ASYNC_STATE,
  onRetryFormulations,
  onRetryRoutes,
  /** @type {(value: string) => Promise<{id: number, value: string}>} */
  onAddFormulation,
  /** @type {(value: string) => Promise<{id: number, value: string}>} */
  onAddRoute
}) => {
  const classes = Styles();

  /** @type {Record<string, import('../../types/dtos/ema').AsyncState<import('../../types/dtos/ema').FormulationDto | import('../../types/dtos/ema').RouteDto>>} */
  const asyncDataMap = { formulations, routes };
  /** @type {Record<string, (() => void) | undefined>} */
  const retryMap = { formulations: onRetryFormulations, routes: onRetryRoutes };
  /** @type {Record<string, ((value: string) => Promise<{id: number, value: string}>) | undefined>} */
  const onAddMap = { formulations: onAddFormulation, routes: onAddRoute };

  const [formData, setFormData] = useState(
    /** @type {{ pno: string, concentration: string, drugname: string, formulations: ({id: number, value: string}|null)[], routes: ({id: number, value: string}|null)[], indication_status: string, packaging: string, packsize: string, strength: string, is_active: boolean }} */
    () => {
      if (flowType === FLOW_TYPE_EDIT && data) {
        return {
          ...data,
          formulations: normalizeToArray(data.formulations),
          routes: normalizeToArray(data.routes)
        };
      }
      return {
        pno: '',
        concentration: '',
        drugname: '',
        formulations: [],
        routes: [],
        indication_status: '',
        packaging: '',
        packsize: '',
        strength: '',
        is_active: true
      };
    }
  );

  const [validationErrors, setValidationErrors] = useState({ formulations: false, routes: false });

  const updateFormData = (formKey, value) => {
    setFormData(prev => ({ ...prev, [formKey]: value }));
  };

  const addItem = field => {
    setFormData(prev => ({ ...prev, [field]: [...prev[field], null] }));
    if (validationErrors[field]) {
      setValidationErrors(prev => ({ ...prev, [field]: false }));
    }
  };

  const removeItem = (field, index) => {
    setFormData(prev => ({
      ...prev,
      [field]: prev[field].filter((_, i) => i !== index)
    }));
  };

  const updateArrayItem = (field, index, value) => {
    setFormData(prev => {
      const arr = [...prev[field]];
      arr[index] = value;
      return { ...prev, [field]: arr };
    });
    if (validationErrors[field]) {
      setValidationErrors(prev => ({ ...prev, [field]: false }));
    }
  };

  const handleSubmit = () => {
    const errors = {
      formulations: formData.formulations.filter(Boolean).length === 0,
      routes: formData.routes.filter(Boolean).length === 0
    };
    if (errors.formulations || errors.routes) {
      setValidationErrors(errors);
      return;
    }
    onSubmit({
      ...formData,
      formulations: formData.formulations.filter(Boolean),
      routes: formData.routes.filter(Boolean)
    });
  };

  const renderInputControl = input => {
    const { id, type, options, placeholder, entityKey, entityLabel } = input;
    if (isLoading) {
      return <Skeleton variant='rect' width={500} height={20} />;
    }
    if (type === INPUT_TYPE_TEXT) {
      return (
        <TextareaAutosize
          padding={1}
          className={classes.textAreaField}
          minRows={2}
          fullWidth
          value={formData[id]}
          onChange={event => updateFormData(id, event.target.value)}
          id={id}
          placeholder={placeholder}
          InputProps={{
            classes: { input: classes.input },
            disableUnderline: true
          }}
        />
      );
    }
    if (type === INPUT_TYPE_SELECT) {
      return (
        <Select
          id={id}
          variant='standard'
          disableUnderline
          required
          MenuProps={{
            anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
            transformOrigin: { vertical: 'top', horizontal: 'left' },
            getContentAnchorEl: null
          }}
          value={formData[id]}
          onChange={event => updateFormData(id, event.target.value)}
          className={classes.dropdownContainer}
          inputProps={{ classes: { icon: classes.icon } }}>
          {options.map(option => (
            <MenuItem value={option.value} key={option.value} className={classes.dropdownText}>
              {option.label}
            </MenuItem>
          ))}
        </Select>
      );
    }
    if (type === INPUT_TYPE_MULTI_ASYNC_SELECT) {
      return (
        <>
          {formData[id].length === 0 && (
            <Typography className={classes.emptyHint}>No {entityLabel} added yet</Typography>
          )}
          {/* eslint-disable react/no-array-index-key */}
          {formData[id].map((item, index) => (
            <Box key={`${id}-${index}`} className={classes.asyncSelectItem}>
              <AsyncSelect
                data={asyncDataMap[entityKey].data}
                loading={asyncDataMap[entityKey].loading}
                error={asyncDataMap[entityKey].error ?? undefined}
                onRetry={retryMap[entityKey]}
                onAdd={onAddMap[entityKey]}
                onChange={val => updateArrayItem(id, index, val)}
                onRemove={() => removeItem(id, index)}
                deletable={index !== 0}
                label={entityLabel}
                validate={() => null}
                value={item}
              />
            </Box>
          ))}
          {/* eslint-enable react/no-array-index-key */}
          <Button
            size='small'
            variant='contained'
            onClick={() => addItem(id)}
            className={classes.addEntityButton}>
            + Add {entityLabel}
          </Button>
          {validationErrors[id] && (
            <Typography className={classes.fieldError}>
              At least one {entityLabel} is required
            </Typography>
          )}
        </>
      );
    }
    return null;
  };

  return (
    <Box padding={1}>
      <Box
        display='flex'
        flexDirection='column'
        justifyContent='space-between'
        className={classes.formScrollContainer}>
        {FORM_INPUTS.map(input => {
          const { id, label } = input;
          return (
            <React.Fragment key={id}>
              <Typography align='left' className={classes.label}>
                {label}
              </Typography>
              {renderInputControl(input)}
            </React.Fragment>
          );
        })}
      </Box>
      <Box padding={1} display='flex' flexDirection='row'>
        <Button className={classes.button} onClick={onCancel} variant='contained'>
          Cancel
        </Button>
        <Button className={classes.button} onClick={handleSubmit} variant='contained'>
          Submit
        </Button>
      </Box>
    </Box>
  );
};

const asyncStatePropType = entityShape =>
  PropTypes.shape({
    data: PropTypes.arrayOf(PropTypes.shape(entityShape)).isRequired,
    loading: PropTypes.bool.isRequired,
    error: PropTypes.string
  });

const dtoShape = { id: PropTypes.number.isRequired, value: PropTypes.string.isRequired };

const optionShape = PropTypes.shape({
  id: PropTypes.number.isRequired,
  value: PropTypes.string.isRequired
});

const productDataShape = PropTypes.shape({
  id: PropTypes.number,
  pno: PropTypes.string,
  concentration: PropTypes.string,
  drugname: PropTypes.string,
  formulations: PropTypes.arrayOf(optionShape),
  routes: PropTypes.arrayOf(optionShape),
  indication_status: PropTypes.string,
  packaging: PropTypes.string,
  packsize: PropTypes.string,
  strength: PropTypes.string,
  is_active: PropTypes.bool
});

EMAProductForm.propTypes = {
  data: productDataShape,
  onCancel: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  isLoading: PropTypes.bool,
  flowType: PropTypes.oneOf([FLOW_TYPE_ADD, FLOW_TYPE_EDIT]),
  formulations: asyncStatePropType(dtoShape),
  routes: asyncStatePropType(dtoShape),
  onRetryFormulations: PropTypes.func,
  onRetryRoutes: PropTypes.func,
  onAddFormulation: PropTypes.func,
  onAddRoute: PropTypes.func
};

EMAProductForm.defaultProps = {
  data: {},
  isLoading: false,
  flowType: FLOW_TYPE_ADD,
  formulations: DEFAULT_ASYNC_STATE,
  routes: DEFAULT_ASYNC_STATE,
  onRetryFormulations: undefined,
  onRetryRoutes: undefined,
  onAddFormulation: undefined,
  onAddRoute: undefined
};
